// Story 6.11 (ADO-1165) — Admin user management client.
//
// Thin typed wrapper over the Bearer-attaching `apiFetch` transport for the
// Story 6.12 admin-user endpoints (FR6/FR7, Auth0 Management API; DD-I.10 = no
// local user table). Mirrors `profile.client.ts` exactly: it is the SINGLE
// consumer seam until the `openapi-typescript`-generated client lands (Story 6.1
// deviation D-1), at which point these calls move to the generated surface.
//
// LIVE: Story 6.12 shipped the backend, and the `/api/v1` global prefix landed
// repo-wide (ADO-1181, closing Story 6.1 D-1) — these API_PREFIX paths resolve
// to live routes. The tests still msw-mock every path.

import { API_PREFIX, apiFetch } from './shoplink-api';

export const ADMIN_USERS_PATH = `${API_PREFIX}/admin/users`;
export const ADMIN_DEPOTS_PATH = `${API_PREFIX}/admin/depots`;

/**
 * Account status as returned by the server (kept in lock-step with the server
 * `AdminUserResponse`, `libs/modules/membership/.../admin-user-response.dto.ts`):
 * `Deactivated` = admin-deactivated (registry); `Locked` = Auth0 brute-force lock
 * (FR10); `Active` = neither.
 *
 * NOTE (Story 6.31 / ADO-1253): the Admin Users TABLE presents only the Figma
 * 2-state model — `Active` and `Locked`. `Deactivated` is a valid wire value (the
 * server still emits it, and `terminateIfDeactivated` acts on it at the session
 * layer) but the table renders it as "Locked" and restores it via the same
 * Unblock path (see `statusView` in `AdminUsers.tsx`). Collapsing the wire
 * contract itself to 2 values is a backend follow-up, not this web-only story.
 */
export type AdminUserStatus = 'Active' | 'Deactivated' | 'Locked';

/** Wire shape of a row from `GET /admin/users` (Story 6.12 contract). */
export interface AdminUser {
  /** User id / Auth0 `sub` — the table `rowKey`. */
  readonly id: string;
  readonly fullName: string;
  readonly email: string;
  readonly role: string;
  /** Depot the user is assigned to; null when unset (see D-4 depot-source note). */
  readonly depotId: string | null;
  readonly accountType: string;
  readonly status: AdminUserStatus;
}

/**
 * One tenant depot from `GET /admin/depots` — Story 6.16 (ADO-1195, OQ-6.11a).
 * `id` is what the create/edit forms submit as `depotId`; `key` is the
 * tenant-registry mapping key (the display label).
 */
export interface AdminDepot {
  readonly id: string;
  readonly key: string;
}

/** Body for `POST /admin/users` (Create user — AC2). */
export interface CreateUserBody {
  readonly fullName: string;
  readonly email: string;
  readonly role: string;
  readonly depotId: string | null;
  readonly accountType: string;
}

/** Body for `PATCH /admin/users/:id` (Edit user — AC3). */
export interface UpdateUserBody {
  readonly fullName: string;
  readonly email: string;
  readonly role: string;
  readonly depotId: string | null;
  readonly accountType: string;
}

/** Body for `PATCH /admin/users/:id/role` (Assign role — role + account type only, AC3). */
export interface AssignRoleBody {
  readonly role: string;
  readonly accountType: string;
}

/** List all users for the current tenant. Throws on a non-2xx so the UI can surface it. */
export async function listUsers(): Promise<AdminUser[]> {
  const res = await apiFetch(ADMIN_USERS_PATH);
  if (!res.ok) throw new Error(`Failed to load users (${res.status})`);
  // Guard the cast: a 2xx with a non-array body (contract drift, a proxy
  // returning 200 HTML, or an envelope) must degrade to the load-error banner,
  // not white-screen the table when it maps over a non-array.
  const data = (await res.json()) as unknown;
  if (!Array.isArray(data)) throw new Error('Unexpected users response shape');
  return data as AdminUser[];
}

/**
 * List the tenant's depots for the create/edit depot Select — Story 6.16
 * (ADO-1195, OQ-6.11a; retires the 6.11 D-4 claim-`depotIds` degradation).
 * Throws on a non-2xx / non-array so the caller can degrade (empty option list).
 */
export async function listDepots(): Promise<AdminDepot[]> {
  const res = await apiFetch(ADMIN_DEPOTS_PATH);
  if (!res.ok) throw new Error(`Failed to load depots (${res.status})`);
  const data = (await res.json()) as unknown;
  if (!Array.isArray(data)) throw new Error('Unexpected depots response shape');
  // Validate element shape too (ADO-1195 review): a well-typed 2xx array can still
  // carry malformed entries. Keep only `{ id, key }` with non-empty string values
  // — an empty `id` would collide with the "no depot" sentinel (`depotId === ''`)
  // and a depot-required role could then slip the client-side gate. Server-side
  // sanitises identically, so this is defence in depth against contract drift.
  return data.filter(
    (d): d is AdminDepot =>
      typeof d === 'object' &&
      d !== null &&
      typeof (d as AdminDepot).id === 'string' &&
      (d as AdminDepot).id.trim() !== '' &&
      typeof (d as AdminDepot).key === 'string' &&
      (d as AdminDepot).key.trim() !== '',
  );
}

/** Create a user (AC2); returns the created row. */
export async function createUser(body: CreateUserBody): Promise<AdminUser> {
  const res = await apiFetch(ADMIN_USERS_PATH, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`Failed to create user (${res.status})`);
  return (await res.json()) as AdminUser;
}

/** Update a user's details (AC3); returns the updated row. */
export async function updateUser(id: string, body: UpdateUserBody): Promise<AdminUser> {
  const res = await apiFetch(`${ADMIN_USERS_PATH}/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`Failed to update user (${res.status})`);
  return (await res.json()) as AdminUser;
}

/** Assign a user's role + account type (AC3); returns the updated row. */
export async function assignRole(id: string, body: AssignRoleBody): Promise<AdminUser> {
  const res = await apiFetch(`${ADMIN_USERS_PATH}/${encodeURIComponent(id)}/role`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`Failed to assign role (${res.status})`);
  return (await res.json()) as AdminUser;
}

// Deactivate/unblock are void-semantic: the caller discards the result and
// re-lists. So they resolve on any 2xx WITHOUT parsing the body — a conventional
// `204 No Content` from Story 6.12 would make `res.json()` throw and misreport a
// SUCCESSFUL destructive action as a failure, inviting a duplicate retry.

/** Deactivate a user (AC4). Server records the action in the auth audit log. */
export async function deactivateUser(id: string): Promise<void> {
  const res = await apiFetch(`${ADMIN_USERS_PATH}/${encodeURIComponent(id)}/deactivate`, {
    method: 'POST',
  });
  if (!res.ok) throw new Error(`Failed to deactivate user (${res.status})`);
}

/** Unblock a locked account (AC4) — resets the failed-sign-in counter server-side. */
export async function unblockUser(id: string): Promise<void> {
  const res = await apiFetch(`${ADMIN_USERS_PATH}/${encodeURIComponent(id)}/unblock`, {
    method: 'POST',
  });
  if (!res.ok) throw new Error(`Failed to unblock account (${res.status})`);
}
